<template>
  <div
    v-if="
      (showBreadcrumb && breadcrumbCombined) ||
      backLink.url ||
      backLink.clickEvent ||
      forceBreadcrumb
    "
    :class="{
      'breadcrumb-fi': true,
      'breadcrumb-fi--catalog-ssr':
        fischer.catalogId() && (!isMounted || !getBreadcrumbByCatalogId) && !error,
    }"
  >
    <grid-container-fi>
      <div class="breadcrumb-fi__container fi:col-span-full">
        <button-fi
          v-if="backLink.url && !backLink.clickEvent"
          class="breadcrumb-fi__back-link button-fi__tertiary button-fi--small"
          data-test="breadcrumb-back-btn"
          natural-events
          :href="backLink.url"
        >
          {{ backLink.text }}
        </button-fi>
        <button-fi
          v-if="backLink.clickEvent"
          :class="{
            'button-fi--small': backLink.text,
            'button-fi__icon': !backLink.text,
            'button-fi__tertiary': backLink.text,
          }"
          class="breadcrumb-fi__back-link"
          :href="backLink.url"
          @click.prevent.stop="backlinkClicked"
        >
          <template v-if="backLink.text">
            {{ backLink.text }}
          </template>
          <template v-else>
            <arrow-icon class="breadcrumb-fi__back-arrow backwards" />
          </template>
        </button-fi>

        <ul
          v-if="breadcrumbCombined.length > 1 || forceBreadcrumb"
          class="breadcrumb-fi__list"
        >
          <li
            v-for="(item, index) in breadcrumbCombined"
            :key="item.url"
            class="breadcrumb-fi__list__item"
          >
            <span
              v-if="index === breadcrumbCombined.length - 1"
              class="breadcrumb-fi__list__item--active"
            >
              {{ item.title }}
            </span>
            <span
              v-else-if="item.notAllowed"
              class="breadcrumb-fi__list__item--not-allowed"
            >
              {{ item.title }}
            </span>
            <a
              v-else
              :href="item.url"
              :title="item.title ?? undefined"
              class="breadcrumb-fi__list__item--link"
            >
              {{ item.title }}
            </a>
          </li>
        </ul>

        <div
          v-if="parentBreadcrumbLink"
          class="breadcrumb-fi__mobile"
        >
          <a
            :href="parentBreadcrumbLink.url"
            :title="parentBreadcrumbLink.title ?? undefined"
            class="breadcrumb-fi__list__item--link breadcrumb-fi__mobile__link"
          >
            <icon-fi
              icon="chevron-left"
              class="breadcrumb-fi__mobile__link__icon"
            />
            {{ parentBreadcrumbLink.title }}
          </a>
        </div>
      </div>
    </grid-container-fi>

    <ld-json
      v-if="breadcrumbCombined.length"
      :json="jsonld"
    />
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { computed, onMounted, ref, watch } from 'vue';

import ArrowIcon from '@/components/atoms/arrow-icon/ArrowIcon.vue';
import ButtonFi from '@/components/atoms/button-fi/ButtonFi.vue';
import IconFi from '@/components/atoms/icon-fi/IconFi.vue';
import LdJson from '@/components/atoms/ld-json/LdJson.vue';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useConstructionBreadcrumb } from '@/composables/constructionBreadcrumb';
import { useFischer } from '@/composables/fischerPlugin';
import { useSalesChannelFilter } from '@/composables/salesChannelFilter';
import type {
  GetBreadcrumbByCatalogIdQuery,
  GetBreadcrumbByCatalogIdQueryVariables,
} from '@/generated/graphql';
import GETBREADCRUMBBYCATALOGID from '@/graphql/getBreadcrumbByCatalogId.gql';
import { useNavigationStore } from '@/store/pinia/navigation';

defineOptions({
  name: 'BreadcrumbFi',
});

const error = ref<boolean>(false);
const navigationStore = useNavigationStore();
const fischer = useFischer();
const { getCDBreadcrumb, getCMBreadcrumb } = useConstructionBreadcrumb();
const { getSalesChannels, isPublicAssortment, supplier } = useSalesChannelFilter();

export interface BackLink {
  url?: string;
  clickEvent?: boolean;
  text?: string;
}

export interface BreadCrumbItem {
  url: string;
  title?: string | null;
  notAllowed?: boolean | null;
}

interface JsonLdListItem {
  '@type': 'ListItem';
  position: number;
  name: string;
  item?: string;
}
interface JsonLd {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: Array<JsonLdListItem>;
}
const props = withDefaults(
  defineProps<{
    backLink?: BackLink;
    breadcrumbData?: Array<BreadCrumbItem>;
    showBreadcrumb?: boolean;
    forceBreadcrumb?: boolean;
  }>(),
  {
    backLink: () => ({}),
    breadcrumbData: () => [],
    showBreadcrumb: true,
  },
);

const isMounted = ref(false);

const isCatalog = computed<boolean>(
  () =>
    fischer.isVariantPage() ||
    fischer.isSeriesPage() ||
    fischer.isFamilyPage() ||
    fischer.isCategoryPage(),
);

onMounted(() => (isMounted.value = true));

const { data: getBreadcrumbByCatalogId, error: queryError } = useQuery<
  GetBreadcrumbByCatalogIdQuery,
  GetBreadcrumbByCatalogIdQueryVariables
>({
  query: GETBREADCRUMBBYCATALOGID,
  variables: computed(() => ({
    channel: fischer.brandId(),
    language: fischer.catalogLocale(),
    catalogId: fischer.catalogId() || '',
    isSeries: fischer.isSeriesPage(),
    isProduct: fischer.isVariantPage(),
    salesChannels: getSalesChannels(),
    publicAssortment: isPublicAssortment(),
    supplier: supplier(),
  })),
  pause: computed(() => !(isMounted.value && isCatalog.value && fischer.catalogId())),
});

watch(queryError, (err) => {
  if (err) {
    console.error(err);
    error.value = true;
  }
});

const productBreadcrumb = computed<Array<BreadCrumbItem>>(() => {
  const items = getBreadcrumbByCatalogId.value?.getBreadcrumbByCatalogId?.breadcrumbItems;
  if (
    !getBreadcrumbByCatalogId.value ||
    getBreadcrumbByCatalogId.value.getBreadcrumbByCatalogId?.notAllowed ||
    !items?.length ||
    (fischer.user.authenticated() && items?.[items.length - 1]?.notAllowed)
  ) {
    return [];
  }
  const productUrl = fischer.pages.products().url;
  return (
    getBreadcrumbByCatalogId.value.getBreadcrumbByCatalogId?.breadcrumbItems?.map((item) => {
      return {
        title: item?.label || '',
        url: item?.url ? productUrl + item.url : '',
        notAllowed: item?.notAllowed,
      };
    }) || []
  );
});

const breadcrumbCombined = computed<Array<BreadCrumbItem>>(() => {
  if (getBreadcrumbByCatalogId.value) {
    return [...props.breadcrumbData, ...productBreadcrumb.value];
  }

  if (getCDBreadcrumb.value) {
    return [...props.breadcrumbData, ...getCDBreadcrumb.value];
  }

  if (getCMBreadcrumb.value) {
    return [...props.breadcrumbData, ...getCMBreadcrumb.value];
  }

  return props.breadcrumbData;
});

const parentBreadcrumbLink = computed<BreadCrumbItem | null>(() => {
  if (breadcrumbCombined.value?.length > 1) {
    return breadcrumbCombined.value[breadcrumbCombined.value.length - 2];
  }

  return null;
});

const jsonld = computed<string>(() => {
  const base = fischer.hostUrlBase();
  const itemListElements = breadcrumbCombined.value.map((el, idx) => {
    const listItem: JsonLdListItem = {
      '@type': 'ListItem',
      position: idx + 1,
      name: el.title ?? '',
    };

    if (idx < breadcrumbCombined.value.length - 1) {
      listItem.item = `${base}${el.url}`;
    }

    return listItem;
  });
  const jsonLdObject: JsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: itemListElements,
  };

  return JSON.stringify(jsonLdObject);
});

watch(productBreadcrumb, (newValue) => {
  navigationStore.setProductBreadcrumb(newValue);
});
const backlinkClicked = () => {
  navigationStore.setBackLinkClicked(true);
};
</script>

<style scoped src="./breadcrumb-fi" />
